import React from 'react';
import {Link} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiFileText,FiBook,FiBarChart,FiSearch,FiPresentation,FiCheckCircle,FiStar,FiEdit} = FiIcons;

const AssignmentSupport = () => {
  const services = [
    {
      icon: FiFileText,
      title: 'Essays & Term Papers',
      description: 'Structured, well-argued, and properly referenced.'
    },
    {
      icon: FiBook,
      title: 'Dissertations & Theses',
      description: 'Comprehensive research, methodology, and analysis.'
    },
    {
      icon: FiBarChart,
      title: 'Case Studies & Reports',
      description: 'Detailed business, management, and academic reports.'
    },
    {
      icon: FiSearch,
      title: 'Research Projects',
      description: 'In-depth academic projects with credible sources.'
    },
    {
      icon: FiPresentation,
      title: 'Presentations & Coursework',
      description: 'Professionally designed and academically strong.'
    },
    {
      icon: FiEdit,
      title: 'Proofreading & Editing',
      description: 'Professional review, corrections, and quality enhancement of existing work.'
    }
  ];

  const benefits = [
    'Better Grades: High-quality assignments improve your academic performance.',
    'Deeper Understanding: Learn from step-by-step solutions and explanations.',
    'Skill Development: Improve your research, writing, and citation skills.',
    'Stress-Free Deadlines: Submit assignments on time without pressure.',
    'Confidence Boost: Feel more prepared and capable for future tasks.',
    'Academic Excellence: Develop critical thinking and analytical skills for long-term success.'
  ];

  const steps = [
    {
      number: '1',
      title: 'Submit Your Assignment Requirements',
      description: 'Provide your instructions, deadline, and details.'
    },
    {
      number: '2',
      title: 'Get Matched with an Expert',
      description: 'We connect you with the most suitable writer for your subject.'
    },
    {
      number: '3',
      title: 'Receive Your Completed Assignment',
      description: 'Plagiarism-free, well-structured, and delivered on time.'
    }
  ];

  const testimonials = [
    {
      text: "Tutor Hub Online helped me with my final year project. The work was original, well-structured, and delivered before the deadline. I scored higher than expected!",
      author: "Maria",
      rating: 5
    },
    {
      text: "Their assignment support service is amazing! The team followed my instructions perfectly and provided a detailed explanation so I could understand the topic better.",
      author: "James",
      rating: 5
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Assignment Support Services – Professional Academic Support
            </h1>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Link
                to="/contact"
                className="bg-accent text-black px-8 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors"
              >
                👉 Order Assignment Support
              </Link>
              <Link
                to="/book-tutor"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary transition-colors"
              >
                👉 Talk to an Expert
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Covered */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Assignment Support Covers</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-sm border hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={service.icon} className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Benefits of Our Assignment Support</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3">
                <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How It Works (3 Easy Steps)</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                👉 Order Now
              </Link>
              <Link
                to="/book-tutor"
                className="border-2 border-primary text-primary px-8 py-3 rounded-lg font-semibold hover:bg-primary hover:text-white transition-colors"
              >
                👉 Talk to an Expert
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Testimonials</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-sm">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">"{testimonial.text}"</p>
                <p className="text-gray-900 font-semibold">– {testimonial.author}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            🎯 Don't struggle with deadlines or complex assignments alone.
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-4xl mx-auto">
            With Tutor Hub Online's Assignment Support Services, you'll get expert-reviewed, support with plagiarism checks and proofreading that boosts your grades and builds your academic skills.
          </p>
          <Link
            to="/book-tutor"
            className="bg-accent text-black px-8 py-4 rounded-lg font-semibold hover:bg-yellow-400 transition-colors text-lg"
          >
            Get Professional Help Today!
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AssignmentSupport;